import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, ShieldCheck, AlertTriangle, ChevronLeft } from "lucide-react";
import { getCandidate, sendEmploymentConfirmation, startOnboarding as startOnboardingApi, resendOnboarding, candidateOnboardingItemFileUrl, getInterviewSessions, inviteToSession, correctInterviewOutcome, getCandidateLink, PUBLIC_TOKEN } from "@/lib/api";
import { StatusPill, DaysLeft, Pill, Banner } from "@/components/ui-bits";
import { AdpEmploymentSetup } from "@/components/AdpEmploymentSetup";
import { COMM_LABEL, ONB_EVENT_LABEL, fmtWhen } from "@/lib/comm";
import { toast } from "sonner";

function Field({ label, children }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function CandidateRecord() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [empBusy, setEmpBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [mondays, setMondays] = useState([]);
  const [selSid, setSelSid] = useState("");
  const [showCorrect, setShowCorrect] = useState(false);
  const [corrOutcome, setCorrOutcome] = useState("attended_pending");
  const [corrReason, setCorrReason] = useState("");

  useEffect(() => {
    getInterviewSessions()
      .then((rows) => setSessions(rows.filter((s) => s.status === "upcoming" || s.status === "live")))
      .catch(() => {});
    getCandidateLink(PUBLIC_TOKEN)
      .then((d) => setMondays(d.sessions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setC(null);
    setNotFound(false);
    getCandidate(registrationId)
      .then(setC)
      .catch(() => setNotFound(true));
  }, [registrationId]);

  if (notFound) return <div className="empty" data-testid="record-not-found">Candidate {registrationId} was not found.</div>;
  if (!c) return <div className="empty" data-testid="record-loading">Loading candidate record…</div>;

  const isFuture = c.status === "future_consideration";
  const isHire = c.status === "hire_approved" || c.status === "converted" || c.convertedToEmployee;
  const isArchived = c.status === "pool_archived";
  const noShowOutcome = (c.interviewOutcomes || []).find((o) => (o.decision === "no_show" || o.showed === false) && !o.corrected);
  const doInvite = async () => {
    if (empBusy || !selSid) return;
    setEmpBusy(true);
    try {
      let payload = {};
      if (selSid.startsWith("id:")) payload = { sessionId: selSid.slice(3) };
      else if (selSid.startsWith("date:")) payload = { sessionDate: selSid.slice(5) };
      const r = await inviteToSession(c.registrationId, payload);
      toast.success(`Interview invite sent — ${r.sessionLabel}.${r.inviteEmailSent === false ? " (email delivery pending)" : ""}`);
      setSelSid("");
      setC(await getCandidate(c.registrationId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send interview invite."); }
    finally { setEmpBusy(false); }
  };

  const invitePicker = (
    <div className="action-row" data-testid="invite-picker">
      <select className="ca-input" value={selSid} onChange={(e) => setSelSid(e.target.value)} data-testid="invite-session-select" style={{ maxWidth: 440 }}>
        <option value="">Select an Interview Session…</option>
        {sessions.map((s) => {
          const isSpecial = s.sessionType === "special_onsite";
          const name = isSpecial ? (s.displayName || "Special On-Site Interview") : s.employerName;
          return <option key={s.id} value={`id:${s.id}`}>{name} — {s.interviewDateLabel}{s.interviewTime ? ` · ${s.interviewTime}` : ""} · {s.status === "live" ? "Live" : "Upcoming"} (Session {s.sessionCode})</option>;
        })}
        {mondays.filter((m) => !sessions.some((s) => s.interviewDate === m.date)).map((m) => (
          <option key={m.date} value={`date:${m.date}`}>Monday Interview — {m.label} · 10:00 AM</option>
        ))}
        <option value="__new__">Create a new session for today</option>
      </select>
      <button className="btn primary" disabled={empBusy || !selSid} onClick={doInvite} data-testid="send-interview-invite-btn">Send Interview Invite</button>
    </div>
  );
  const doCorrect = async () => {
    if (empBusy || !noShowOutcome || !corrReason.trim()) return;
    setEmpBusy(true);
    try {
      await correctInterviewOutcome(c.registrationId, { sessionId: noShowOutcome.sessionId, correctedOutcome: corrOutcome, reason: corrReason.trim() });
      toast.success("Interview outcome corrected — original No Show preserved in the audit.");
      setShowCorrect(false); setCorrReason("");
      setC(await getCandidate(c.registrationId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not correct the outcome."); }
    finally { setEmpBusy(false); }
  };
  const hireMeta = [c.position, c.hireShift, c.startDateLabel].filter(Boolean).join(" · ");
  const beginOnboarding = async () => {
    if (empBusy) return;
    setEmpBusy(true);
    try {
      await startOnboardingApi(c.registrationId, { startedBy: "Make It Happen Philly" });
      toast.success("MIHP onboarding started — secure link issued to the candidate.");
      setC(await getCandidate(c.registrationId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start onboarding.");
    } finally { setEmpBusy(false); }
  };
  const sendEmpConf = async () => {
    if (empBusy) return;
    setEmpBusy(true);
    try {
      const r = await sendEmploymentConfirmation(c.registrationId, { issuedBy: "Make It Happen Philly" });
      toast.success(r.emailSent ? "Employment Confirmation sent to the candidate." : "Employment Confirmation recorded (email not delivered — status unchanged).");
      const fresh = await getCandidate(c.registrationId);
      setC(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue the Employment Confirmation.");
    } finally { setEmpBusy(false); }
  };
  const resendLink = async () => {
    if (empBusy) return;
    setEmpBusy(true);
    try {
      const r = await resendOnboarding(c.registrationId);
      toast.success(r.emailSent ? "Onboarding link re-sent — same packet, no items reset." : "Onboarding link re-issued (email not delivered). Same packet, no items reset.");
      setC(await getCandidate(c.registrationId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend the onboarding link.");
    } finally { setEmpBusy(false); }
  };

  return (
    <div data-testid="candidate-record">
      <button className="btn sm ghost" onClick={() => navigate(-1)} data-testid="record-back-btn" style={{ marginBottom: 12 }}>
        <ChevronLeft size={15} style={{ verticalAlign: "-3px" }} /> Back
      </button>
      <div className="crumb">
        <Link to="/">Staffing Home</Link> › <Link to="/candidate-pool">Candidate Pool</Link> ›{" "}
        <strong>{c.registrationId}</strong>
      </div>

      <div className="record-head" data-testid="record-head">
        <div>
          <h1 className="record-title">{c.name}</h1>
          <div className="mut record-sub">{c.registrationId} · {c.employerName}</div>
          {isHire && hireMeta && <div className="mut record-sub" data-testid="record-hire-meta">{hireMeta}</div>}
          <div className="record-meta" data-testid="record-meta">
            <StatusPill status={c.status} label={c.statusLabel} />
            {c.convertedToEmployee ? (
              <span className="meta-chip" data-testid="record-pool-age">
                <span className="mc-k">Employee</span>
              </span>
            ) : c.isActivePool ? (
              <span className="meta-chip" data-testid="record-pool-age">
                <span className="mc-k">Active pool</span>
                <DaysLeft days={c.daysLeftInPool} total={c.poolDays} />
                <span className="mc-v">of {c.poolDays} days</span>
              </span>
            ) : (
              <span className="meta-chip" data-testid="record-pool-age">
                <span className="mc-k">Archived — Inactive</span>
              </span>
            )}
          </div>
          {c.interviewAssignment && (
            <div className="record-assign" data-testid="record-interview-assignment">
              <span className="ra-lead">Interview: <strong>{c.interviewAssignment.dateLabel} · {c.interviewAssignment.time}</strong></span>
              <span className="ra-emp">{c.interviewAssignment.employerName}</span>
              <span className="ra-sid mut">Session {c.interviewAssignment.sessionCode} · {c.interviewAssignment.status === "live" ? "Live" : "Upcoming"}</span>
            </div>
          )}
        </div>
        <button className="btn sm ghost" data-testid="add-note-btn">Add note</button>
      </div>

      {isFuture && (
        <Banner variant="neutral" icon={<ClipboardCheck size={18} />}>
          <strong>Future Consideration.</strong> Already completed Interview + Factory Preview. Not
          rejected — there was no current vacancy at decision time. Retained as a ready,
          previously-interviewed candidate to reconsider when staffing needs or shifts change.
          {(c.reconsiderDateLabel || c.futureReason) && (
            <span data-testid="record-reconsider-info"> {c.futureReason ? c.futureReason : ""}
              {c.reconsiderDateLabel ? ` · reconsider ${c.reconsiderDateLabel}` : ""}.</span>
          )}
        </Banner>
      )}

      <div className="cols">
        <div className="panel" data-testid="candidate-details">
          <div className="ph"><h2>Candidate</h2><span className="sp" /></div>
          <div className="pb">
            <dl className="dl">
              <Field label="Registration ID">{c.registrationId}</Field>
              <Field label="Preferred shift">{c.preferredShift}</Field>
              <Field label="Email">{c.email}</Field>
              <Field label="Phone">{c.phone}</Field>
              <Field label="Registered via">{c.registeredViaLabel}</Field>
              {c.photoIdAtInterview && <Field label="Can bring photo ID to interview">{c.photoIdAtInterview}</Field>}
              {c.workAuthDocs && <Field label="Can provide work-authorization documents (MIHP-internal)">{c.workAuthDocs}</Field>}
              <Field label="Days left in pool">
                {c.isActivePool
                  ? <span className="days-inline"><DaysLeft days={c.daysLeftInPool} total={c.poolDays} /> <span className="mut">of {c.poolDays}</span></span>
                  : <span className="mut">Archived</span>}
              </Field>
            </dl>
          </div>
        </div>

        <div className="panel" data-testid="candidate-resume">
          <div className="ph">
            <h2>Résumé</h2><span className="sp" />
            <Pill variant="crit">Restricted</Pill>
          </div>
          <div className="pb">
            {c.resume ? (
              <>
                <dl className="dl one-col">
                  <Field label="File">{c.resume.file} · {c.resume.sizeKb} KB</Field>
                  <Field label="Uploaded">{c.resume.uploadedLabel}</Field>
                  <Field label="Available until">
                    {c.resume.availableUntilLabel} <span className="mut">(end of active pool)</span>
                  </Field>
                </dl>
                <div className="resume-actions">
                  <button className="btn" data-testid="open-resume-btn">Open résumé</button>
                </div>
                <p className="secure-note" data-testid="resume-secure-note">
                  <ShieldCheck size={15} />
                  Access is restricted and every view is audited. Never public, never served from a
                  guessable address, and unavailable once the candidate ages out of the active pool.
                </p>
              </>
            ) : (
              <p className="secure-note" data-testid="resume-empty-note">
                <ShieldCheck size={15} />
                No résumé on file. Any uploaded document is access-restricted, audited on every view, and
                never publicly reachable.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="panel" data-testid="workflow-history">
        <div className="ph">
          <h2>Workflow history</h2>
          <span className="sp" />
          <span className="mut">preserved in full — never rewritten</span>
        </div>
        <div className="pb">
          {c.workflowHistory && c.workflowHistory.length ? (
            <ul className="trail">
              {c.workflowHistory.map((h, i) => (
                <li key={i} className={h.kind === "orig" ? "orig" : ""}>
                  <div className="tt">{h.title}</div>
                  <div className="tm">{h.meta}</div>
                  {h.note && <div className="tw">{h.note}</div>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mut">No recorded history yet for this candidate.</p>
          )}
        </div>
      </div>

      <div className="panel" data-testid="communication-history">
        <div className="ph">
          <h2>Communication history</h2>
          <span className="sp" />
          <span className="mut">actual email sends — never inferred from status</span>
        </div>
        <div className="pb">
          {c.communications && c.communications.length ? (
            <div className="comm-list">
              {c.communications.map((m) => (
                <div key={m.id} className="comm-item" data-testid={`comm-item-${m.id}`}
                  style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line-2)" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: ".875rem" }}>
                      {COMM_LABEL[m.type] || m.type}{" "}
                      <span className={`pill ${m.kind === "resend" ? "awaiting" : ""}`} style={{ marginLeft: 6 }} data-testid={`comm-kind-${m.id}`}>
                        {m.kind === "resend" ? "Resend" : "Original"}
                      </span>
                    </div>
                    <div className="mut" style={{ fontSize: ".8125rem", marginTop: 2 }}>
                      To {m.recipientEmail}
                      {m.sessionCode ? ` · Session ${m.sessionCode}` : ""}
                      {m.onboardingEvent ? ` · ${ONB_EVENT_LABEL[m.onboardingEvent] || m.onboardingEvent}` : ""}
                    </div>
                    <div className="mut" style={{ fontSize: ".75rem", marginTop: 2 }}>
                      Created {fmtWhen(m.createdAt)} · Sent {fmtWhen(m.sentAt)}
                    </div>
                    {m.error && <div style={{ fontSize: ".75rem", color: "var(--mihp-red, #B91C1C)", marginTop: 2 }} data-testid={`comm-error-${m.id}`}>{m.error}</div>}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <span className={`pill ${m.result === "sent" ? "approved" : "crit"}`} data-testid={`comm-result-${m.id}`}>
                      {m.result === "sent" ? "Sent ✓" : "Failed"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mut" data-testid="comm-none">No emails have been sent to this candidate yet.</p>
          )}
        </div>
      </div>

      <div className="panel" data-testid="available-actions">
        <div className="ph"><h2>Available actions</h2><span className="sp" /></div>
        <div className="pb">
          {isHire ? (
            <div className="actions-wrap" data-testid="hire-actions">
              <button className="onboard-cta" onClick={sendEmpConf} disabled={empBusy} data-testid="send-employment-confirmation-btn">
                {empBusy ? "Sending…" : "Send Employment Confirmation"}
              </button>
              <p className="mut onboard-help">Issues the candidate-facing Employment Confirmation (approved for employment, subject to completing onboarding). Emails the candidate and records it on this record. Hire Approved status is unchanged.</p>
              {(c.employmentConfirmations && c.employmentConfirmations.length > 0) && (
                <div className="emp-conf-log" data-testid="employment-confirmation-log" style={{ marginTop: 14, borderTop: "1px dashed var(--line)", paddingTop: 12 }}>
                  {c.employmentConfirmations.map((e) => (
                    <div key={e.id} style={{ fontSize: ".8125rem", color: "var(--ink-2)", marginBottom: 8 }} data-testid={`emp-conf-${e.id}`}>
                      <strong>Employment Confirmation issued</strong> — {e.position} · {e.shift} · {e.schedule} · start {e.startDate}
                      <div className="mut">Sent to {e.recipient} by {e.issuedBy} · email {e.emailSent ? "sent" : "not delivered"}</div>
                    </div>
                  ))}
                </div>
              )}
              {c.onboarding?.started && (
                <div className="emp-conf-log" data-testid="onboarding-summary" style={{ marginTop: 14, borderTop: "1px dashed var(--line)", paddingTop: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: ".9375rem" }}>Onboarding — {c.onboarding.mihpComplete ? "MIHP Requirements Complete" : "In Progress"}</div>
                  <div className="mut" style={{ fontSize: ".8125rem", marginTop: 4 }}>
                    Packet: {c.onboarding.packetName} · started by {c.onboarding.startedBy} · link {c.onboarding.emailSent ? "emailed" : "issued"} · {c.onboarding.items.filter((i) => i.status === "complete").length}/{c.onboarding.items.length} items complete
                  </div>
                  <a className="btn sm" href={`/onboarding/${c.onboarding.token}`} target="_blank" rel="noreferrer" data-testid="open-onboarding-link" style={{ marginTop: 8 }}>Open candidate onboarding link</a>
                  <button className="btn sm" onClick={resendLink} disabled={empBusy} data-testid="resend-onboarding-btn" style={{ marginTop: 8, marginLeft: 8 }}>Resend onboarding link</button>
                  <div className="onb-rec-items" data-testid="onboarding-items" style={{ marginTop: 12 }}>
                    {c.onboarding.items.map((it) => {
                      const doneLabel = it.action === "signature" ? "signed" : it.action === "acknowledgment" ? "acknowledged" : "reviewed";
                      const ver = it.completion?.version || it.version;
                      return (
                        <div key={it.itemId} data-testid={`onb-rec-item-${it.itemId}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: ".8125rem", padding: "6px 0", borderBottom: "1px solid var(--line-2)" }}>
                          <span>{it.title} <span className="mut">v{ver}</span>{" "}
                            {it.status === "complete"
                              ? <span style={{ color: "var(--mihp-green)" }}>· {doneLabel}</span>
                              : <span className="mut">· pending</span>}
                          </span>
                          {it.fileName
                            ? <a className="btn sm ghost" href={candidateOnboardingItemFileUrl(c.registrationId, it.itemId)} target="_blank" rel="noreferrer" data-testid={`onb-rec-view-${it.itemId}`}>View document</a>
                            : <span className="mut">no file</span>}
                        </div>
                      );
                    })}
                  </div>
                  {c.onboarding.mihpComplete && <div className="mut" style={{ fontSize: ".8125rem", marginTop: 8 }}>✓ MIHP portion complete — <strong>Continue to ADP Employment Setup</strong> (next phase). Not converted to employee.</div>}
                </div>
              )}
              {c.onboarding?.mihpComplete && <AdpEmploymentSetup candidate={c} reload={async () => setC(await getCandidate(c.registrationId))} />}
              <details className="more-actions" data-testid="more-actions" style={{ marginTop: 12 }}>
                <summary>Begin Onboarding &amp; more</summary>
                <div className="action-row">
                  <button className="btn" onClick={beginOnboarding} disabled={empBusy} data-testid="begin-onboarding-btn">{c.onboarding?.started ? "Re-issue Onboarding" : "Begin Onboarding"}</button>
                  <button className="btn">Add note</button>
                  <button className="btn danger-outline">Record Not Selected</button>
                  <button className="btn danger-outline">Record Withdrawn</button>
                </div>
                <p className="mut" style={{ marginTop: 8, fontSize: ".75rem" }}>Onboarding will include MIHP requirements and employment setup (ADP / prehire handoff) in a later step. Hire Approved is not the same as fully onboarded or converted to an employee.</p>
              </details>
            </div>
          ) : isFuture ? (
            <>
              <div className="actions-wrap">
                <div className="action-row">
                  <button className="btn">Reconsider for a vacancy</button>
                  <button className="btn">Add note</button>
                </div>
                <div className="action-sep" />
                <p className="mut" style={{ marginTop: 0 }}>Send a fresh interview invite (recurring Monday or Special On-Site):</p>
                {invitePicker}
                <div className="action-sep" />
                <div className="action-row consequential">
                  <button className="btn danger-outline">Record Not Selected</button>
                  <button className="btn danger-outline">Record Withdrawn</button>
                </div>
              </div>
              <Banner variant="crit" icon={<AlertTriangle size={18} />} style={{ marginTop: 16, marginBottom: 0 }}>
                There is deliberately <strong>no "return to Interview Pending"</strong> action. This
                candidate has already completed their Interview + Factory Preview; moving them back to the
                pending queue would misrepresent them as someone still waiting to interview and would
                destroy that distinction.
              </Banner>
            </>
          ) : isArchived ? (
            <div className="actions-wrap" data-testid="archived-actions">
              <p className="mut" style={{ marginTop: 0 }}>This candidate is <strong>Archived — Inactive</strong>{c.archivedReason ? ` (${c.archivedReason})` : ""}. Reactivate by inviting them to an Interview Session — the same canonical record returns to Interview Pending and prior history (including any No Show) is preserved.</p>
              {noShowOutcome && (
                <p className="mut" data-testid="record-noshow-outcome" style={{ fontSize: ".8125rem" }}>
                  Prior outcome: <strong>Interview — No Show</strong> · {noShowOutcome.sessionLabel} · {noShowOutcome.employerName} <span className="mut">(Session {noShowOutcome.sessionCode})</span>
                </p>
              )}
              {invitePicker}
              {noShowOutcome && (
                <>
                  <div className="action-sep" />
                  <button className="btn" onClick={() => setShowCorrect(true)} data-testid="correct-outcome-btn">Correct Interview Outcome</button>
                  <p className="mut" style={{ fontSize: ".75rem", marginTop: 6 }}>Use only if the No Show was recorded in error. The original outcome is preserved in the session audit — it is never erased.</p>
                </>
              )}
            </div>
          ) : (
            <div className="actions-wrap" data-testid="waiting-actions">
              {c.interviewAssignment && (
                <div className="assigned-note" data-testid="record-assigned-note">
                  <div>Selected session: <strong>{c.interviewAssignment.sessionName || c.interviewAssignment.employerName}</strong> — {c.interviewAssignment.dateLabel} · {c.interviewAssignment.time} <span className="mut">(Session {c.interviewAssignment.sessionCode}, {c.interviewAssignment.status === "live" ? "Live" : "Upcoming"})</span></div>
                  <div style={{ marginTop: 4 }} data-testid="record-confirmation-status">
                    Confirmation status: <strong>{c.interviewAssignment.registrationStatus}</strong>
                    {c.interviewAssignment.confirmationCode
                      ? <> · Confirmation Code <strong data-testid="record-confirmation-code">{c.interviewAssignment.confirmationCode}</strong></>
                      : <span className="mut"> · awaiting MIHP confirmation code</span>}
                  </div>
                </div>
              )}
              <p className="mut" style={{ marginTop: c.interviewAssignment ? 8 : 0 }}>Send a fresh interview invitation — choose a recurring Monday or a Special On-Site Interview. Sending assigns this same candidate to the selected session (reassigning it as the current interview if they already had a later one); prior assignment history is preserved.</p>
              {invitePicker}
              <div className="action-sep" />
              <div className="action-row consequential">
                <button className="btn danger-outline">Record Not Selected</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCorrect && (
        <div className="modal-scrim" onClick={() => setShowCorrect(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="correct-outcome-modal" style={{ maxWidth: 520 }}>
            <div className="modal-head"><h2>Correct Interview Outcome</h2></div>
            <div className="modal-body">
              <p className="mut" style={{ marginTop: 0 }}>Correcting a No Show recorded in error. The original No Show is preserved in the session correction audit and is never deleted.</p>
              <label className="oa-lbl">Corrected outcome
                <select className="ca-input" value={corrOutcome} onChange={(e) => setCorrOutcome(e.target.value)} data-testid="correct-outcome-select">
                  <option value="attended_pending">Attended — restore to Interview Pending</option>
                  <option value="not_selected">Not Selected (attended, not hired)</option>
                </select>
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Reason (required)
                <input className="ca-input" value={corrReason} onChange={(e) => setCorrReason(e.target.value)} data-testid="correct-reason" placeholder="e.g. Candidate did attend — logged under the wrong record" />
              </label>
              <button className="mh-save" disabled={empBusy || !corrReason.trim()} onClick={doCorrect} data-testid="correct-submit" style={{ marginTop: 12 }}>{empBusy ? "Saving…" : "Apply correction"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
